'use client';

import Link from 'next/link';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Better Auth uses `forgetPassword` endpoint via direct fetch
    // since the client method name varies by version.
    const result = await authClient.requestPasswordReset({
      email,
      redirectTo: '/reset-password',
    });

    setIsLoading(false);

    if (result.error) {
      setError(result.error.message ?? 'Failed to send reset email. Please try again.');
      return;
    }

    setIsSent(true);
  }

  if (isSent) {
    return (
      <div className="w-full max-w-sm">
        <div className="rounded-lg bg-gray-800 p-8 text-center">
          <h2 className="mb-4 text-xl font-semibold text-gray-100">Check your email</h2>
          <p className="mb-4 text-sm text-gray-400">
            If an account exists for <span className="font-medium text-gray-200">{email}</span>, we sent a password
            reset link. Check your inbox.
          </p>
          <Link href="/sign-in" className="text-sm text-primary-500 hover:text-primary-400">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm">
      <div className="rounded-lg bg-gray-800 p-8">
        <h2 className="mb-2 text-center text-xl font-semibold text-gray-100">Forgot your password?</h2>
        <p className="mb-6 text-center text-sm text-gray-400">Enter your email and we&apos;ll send you a reset link.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-300">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-500"
            />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <Button type="submit" disabled={isLoading} className="w-full bg-primary-700 hover:bg-primary-600">
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <Link href="/sign-in" className="text-sm text-gray-400 hover:text-primary-500">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
